import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, GripVertical } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { templateService } from '../services/templateService';
import type { QuestionnaireTemplate, QuestionnaireField } from '../types/questionnaires';

interface CreateTemplateModalProps {
  template?: QuestionnaireTemplate | null;  // For editing
  onClose: () => void;
  onSaved: () => void;
}

const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({ 
  template, 
  onClose, 
  onSaved 
}) => {
  const { user, userProfile } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fields, setFields] = useState<QuestionnaireField[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (template) {
      // Editing mode - load template data
      setName(template.name);
      setDescription(template.description || '');
      setFields([...template.fields].sort((a, b) => a.order - b.order));
    }
  }, [template]);

  const addField = () => {
    const newField: QuestionnaireField = {
      id: `field_${Date.now()}`,
      label: '',
      type: 'text',
      required: false,
      order: fields.length
    };
    setFields([...fields, newField]);
  };

  const updateField = (index: number, updates: Partial<QuestionnaireField>) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], ...updates };
    setFields(updated);
  };

  const removeField = (index: number) => {
    const updated = fields.filter((_, i) => i !== index);
    // Reorder remaining fields
    updated.forEach((field, i) => field.order = i);
    setFields(updated);
  };

  const moveFieldUp = (index: number) => {
    if (index === 0) return;
    const updated = [...fields];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    updated.forEach((field, i) => field.order = i);
    setFields(updated);
  };

  const moveFieldDown = (index: number) => {
    if (index === fields.length - 1) return;
    const updated = [...fields];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    updated.forEach((field, i) => field.order = i);
    setFields(updated);
  };

  const generateFieldId = (label: string): string => {
    // Convert label to kebab-case ID
    return label
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleSave = async () => {
    // Validation
    if (!name.trim()) {
      alert('Bitte geben Sie einen Namen ein');
      return;
    }

    if (fields.length === 0) {
      alert('Bitte fügen Sie mindestens ein Feld hinzu');
      return;
    }

    const invalidFields = fields.filter(f => !f.label.trim());
    if (invalidFields.length > 0) {
      alert('Alle Felder müssen ein Label haben');
      return;
    }

    if (!user || !userProfile) return;

    try {
      setIsSaving(true);

      // Generate IDs from labels if not set
      const processedFields = fields.map(field => ({
        ...field,
        id: field.id.startsWith('field_') ? generateFieldId(field.label) : field.id,
        label: field.label.trim(),
        placeholder: field.placeholder?.trim() || undefined,
        helpText: field.helpText?.trim() || undefined
      }));

      const templateData = {
        name: name.trim(),
        description: description.trim() || undefined,
        fields: processedFields,
        createdBy: user.uid,
        createdByName: userProfile.displayName,
        isSystem: false
      };

      if (template?.id) {
        // Update existing
        await templateService.updateTemplate(template.id, templateData);
        alert('Vorlage erfolgreich aktualisiert!');
      } else {
        // Create new
        await templateService.createTemplate(templateData);
        alert('Vorlage erfolgreich erstellt!');
      }

      onSaved();
      onClose();

    } catch (error) {
      console.error('Error saving template:', error);
      alert('Fehler beim Speichern der Vorlage');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-2xl font-bold text-gray-900">
            {template ? 'Vorlage bearbeiten' : 'Neue Vorlage erstellen'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Template Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Vorlagen-Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. 'Kundenumfrage', 'Team-Onboarding'"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              disabled={template?.isSystem}
            />
            {template?.isSystem && (
              <p className="text-xs text-gray-500 mt-1">
                System-Vorlagen können nicht umbenannt werden
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Beschreibung (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Wofür wird diese Vorlage verwendet?"
              rows={2}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Fields Section */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-semibold text-gray-900">
                Felder <span className="text-red-500">*</span>
              </label>
              <button
                onClick={addField}
                className="text-purple-600 hover:text-purple-700 flex items-center gap-2 text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Feld hinzufügen
              </button>
            </div>

            {fields.length === 0 ? (
              <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <p className="text-gray-600">Noch keine Felder definiert</p>
                <button
                  onClick={addField}
                  className="mt-4 text-purple-600 hover:text-purple-700 font-medium"
                >
                  Erstes Feld hinzufügen
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start gap-4">
                      {/* Reorder Buttons */}
                      <div className="flex flex-col gap-1 pt-2">
                        <button
                          onClick={() => moveFieldUp(index)}
                          disabled={index === 0}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs"
                          title="Nach oben"
                        >
                          ▲
                        </button>
                        <GripVertical className="w-5 h-5 text-gray-400" />
                        <button
                          onClick={() => moveFieldDown(index)}
                          disabled={index === fields.length - 1}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs"
                          title="Nach unten"
                        >
                          ▼
                        </button>
                      </div>

                      {/* Field Config */}
                      <div className="flex-1 space-y-3">
                        {/* Field Label */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Label <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={field.label}
                            onChange={(e) => updateField(index, { label: e.target.value })}
                            placeholder="z.B. 'Projekttitel', 'Beschreibung'"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-purple-500"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          {/* Field Type */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Typ
                            </label>
                            <div className="flex flex-col gap-2">
                              <label className="flex items-center">
                                <input
                                  type="radio"
                                  checked={field.type === 'text'}
                                  onChange={() => updateField(index, { type: 'text' })}
                                  className="mr-2"
                                />
                                <span className="text-sm">Text</span>
                              </label>
                              <label className="flex items-center">
                                <input
                                  type="radio"
                                  checked={field.type === 'textarea'}
                                  onChange={() => updateField(index, { type: 'textarea' })}
                                  className="mr-2"
                                />
                                <span className="text-sm">Textarea</span>
                              </label>
                            </div>
                          </div>

                          {/* Required Checkbox */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Optionen
                            </label>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={field.required}
                                onChange={(e) => updateField(index, { required: e.target.checked })}
                                className="mr-2"
                              />
                              <span className="text-sm font-medium text-gray-700">
                                Pflichtfeld
                              </span>
                            </label>
                          </div>
                        </div>

                        {/* Placeholder */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Platzhalter (optional)
                          </label>
                          <input
                            type="text"
                            value={field.placeholder || ''}
                            onChange={(e) => updateField(index, { placeholder: e.target.value })}
                            placeholder="Hinweistext im Feld..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => removeField(index)}
                        className="text-red-600 hover:text-red-700 p-2"
                        title="Feld entfernen"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !name.trim() || fields.length === 0}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Speichert...' : template ? 'Aktualisieren' : 'Erstellen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTemplateModal;

